"use client";

import { Bot, FileSearch, User } from "lucide-react";
import { useQuery } from "convex/react";
import { makeFunctionReference } from "convex/server";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { QuerySkeleton } from "@/components/ui/skeleton";
import { getEmptyListState } from "@/core/ui/empty-states";

export interface AgentTimelineData {
  agentActions: Array<{
    _id: string;
    action: string;
    status: string;
    permissionDecision: string;
    createdAt: number;
    output?: Record<string, unknown>;
  }>;
  auditEvents: Array<{
    _id: string;
    eventType: string;
    actorType: string;
    summary: string;
    createdAt: number;
    payload: Record<string, unknown>;
  }>;
  evidence: Array<{
    _id: string;
    type: string;
    source: string;
    createdAt: number;
    payload: Record<string, unknown>;
  }>;
  agentDecisions?: Array<{
    _id?: string;
    decisionId: string;
    decision: string;
    confidenceScore: number;
    riskScore: number;
    reasoningSummary: string;
    recommendedNextAction: string;
    requiredApproval: boolean;
    policyChecks: Array<Record<string, unknown>>;
    toolCalls: string[];
    createdAt: number;
  }>;
  toolCallLogs?: Array<{
    _id?: string;
    toolCallId: string;
    toolName: string;
    status: string;
    permissionDecision: string;
    error?: string;
    output?: Record<string, unknown>;
    createdAt: number;
  }>;
}

const timelineQuery = makeFunctionReference<
  "query",
  { tenantId: string; entityType: "load" | "carrier" | "customer"; entityId: string },
  AgentTimelineData
>("timeline:forEntity");

export function AgentTimeline({
  entityType,
  entityId,
  tenantId,
  fallback,
}: {
  entityType: "load" | "carrier" | "customer";
  entityId: string;
  tenantId: string;
  fallback?: AgentTimelineData;
}) {
  if (!process.env.NEXT_PUBLIC_CONVEX_URL) {
    return <AgentTimelinePanel data={fallback ?? emptyTimeline()} />;
  }

  return (
    <LiveAgentTimeline
      entityType={entityType}
      entityId={entityId}
      tenantId={tenantId}
      fallback={fallback}
    />
  );
}

function LiveAgentTimeline({
  entityType,
  entityId,
  tenantId,
  fallback,
}: {
  entityType: "load" | "carrier" | "customer";
  entityId: string;
  tenantId: string;
  fallback?: AgentTimelineData;
}) {
  const data = useQuery(timelineQuery, { tenantId, entityType, entityId });
  if (data === undefined && !fallback) {
    return <QuerySkeleton className="sticky top-4" surface="agentTimeline" />;
  }
  return <AgentTimelinePanel data={data ?? fallback ?? emptyTimeline()} />;
}

function AgentTimelinePanel({ data }: { data: AgentTimelineData }) {
  const entries = normalizeEntries(data);

  return (
    <Card className="sticky top-4">
      <CardHeader className="p-3">
        <CardTitle className="text-sm">Agent Timeline</CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        {entries.length === 0 ? (
          <EmptyState className="p-4" {...getEmptyListState("timeline")} />
        ) : (
          <ol className="space-y-4">
            {entries.map((entry) => (
              <li key={entry.id} className="relative border-l border-border pl-4">
                <span className="absolute -left-[7px] top-1 rounded-full bg-card">
                  {entry.kind === "agent" ? (
                    <Bot size={14} className="text-cyan-700" />
                  ) : entry.kind === "evidence" ? (
                    <FileSearch size={14} className="text-amber-700" />
                  ) : (
                    <User size={14} className="text-muted-foreground" />
                  )}
                </span>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">{formatDate(entry.createdAt)}</div>
                    <div className="truncate text-sm font-medium">{entry.title}</div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                    {entry.simulated ? <Badge variant="warn">Simulated — not sent</Badge> : null}
                    <Badge variant={entry.badgeVariant}>{entry.badge}</Badge>
                  </div>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{entry.summary}</p>
                {entry.reasoning ? (
                  <details className="mt-2 rounded-md border border-border px-2 py-1 text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Reasoning</summary>
                    <pre className="mt-2 whitespace-pre-wrap font-sans text-foreground">{entry.reasoning}</pre>
                  </details>
                ) : null}
                {entry.evidenceChips.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {entry.evidenceChips.map((chip) => (
                      <span key={chip} className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        {chip}
                      </span>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function normalizeEntries(data: AgentTimelineData) {
  return [
    ...data.agentActions.map((action) => ({
      id: action._id,
      kind: "agent" as const,
      createdAt: action.createdAt,
      title: action.action,
      summary: `Agent action ${action.status}`,
      badge: action.permissionDecision,
      badgeVariant: decisionVariant(action.permissionDecision),
      reasoning: stringFromPayload(action.output?.reasoning),
      evidenceChips: payloadEvidence(action.output),
      simulated: hasSandboxMarker(action.output),
    })),
    ...data.auditEvents.map((event) => ({
      id: event._id,
      kind: event.actorType === "tma" ? ("agent" as const) : ("human" as const),
      createdAt: event.createdAt,
      title: event.eventType,
      summary: event.summary,
      badge: event.actorType,
      badgeVariant: "neutral" as const,
      reasoning: stringFromPayload(event.payload.reasoning),
      evidenceChips: payloadEvidence(event.payload),
      simulated: hasSandboxMarker(event.payload),
    })),
    ...data.evidence.map((evidence) => ({
      id: evidence._id,
      kind: "evidence" as const,
      createdAt: evidence.createdAt,
      title: evidence.type,
      summary: `Evidence from ${evidence.source}`,
      badge: "evidence",
      badgeVariant: "info" as const,
      reasoning: stringFromPayload(evidence.payload.reasoning),
      evidenceChips: [evidence.source],
      simulated: false,
    })),
    ...(data.agentDecisions ?? []).map((decision) => ({
      id: decision._id ?? decision.decisionId,
      kind: "agent" as const,
      createdAt: decision.createdAt,
      title: `Decision: ${decision.decision}`,
      summary: decision.recommendedNextAction,
      badge: decision.requiredApproval ? "approval gated" : decision.decision,
      badgeVariant: decisionVariant(decision.decision),
      reasoning: decision.reasoningSummary,
      evidenceChips: [
        `${decision.riskScore}% risk`,
        `${decision.confidenceScore}% confidence`,
        ...decision.toolCalls,
      ].slice(0, 4),
      simulated: false,
    })),
    ...(data.toolCallLogs ?? []).map((tool) => ({
      id: tool._id ?? tool.toolCallId,
      kind: "agent" as const,
      createdAt: tool.createdAt,
      title: `Tool: ${tool.toolName}`,
      summary: tool.error ?? `Tool call ${tool.status}; no unsafe external action sent.`,
      badge: tool.permissionDecision,
      badgeVariant: decisionVariant(tool.permissionDecision),
      reasoning: stringFromPayload(tool.output?.reasoning),
      evidenceChips: [tool.status],
      simulated: hasSandboxMarker(tool.output),
    })),
  ].sort((left, right) => right.createdAt - left.createdAt);
}

/** Detects the provider sandbox marker (rawProviderResponse.sandbox === true)
 * anywhere inside a command attempt result — the execution never left the sandbox. */
function hasSandboxMarker(value: unknown, depth = 0): boolean {
  if (!value || typeof value !== "object" || depth > 5) return false;
  const record = value as Record<string, unknown>;
  if (record.sandbox === true) return true;
  return Object.values(record).some((child) => hasSandboxMarker(child, depth + 1));
}

function payloadEvidence(payload: Record<string, unknown> | undefined): string[] {
  if (!payload) return [];
  return Object.entries(payload)
    .filter(([key]) => key.toLowerCase().includes("evidence"))
    .map(([key]) => key)
    .slice(0, 4);
}

function stringFromPayload(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function decisionVariant(decision: string): "neutral" | "good" | "warn" | "bad" | "info" {
  if (decision === "allow") return "good";
  if (decision === "approval_required") return "warn";
  if (decision === "block") return "bad";
  return "neutral";
}

function formatDate(value: number) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(value);
}

function emptyTimeline(): AgentTimelineData {
  return { agentActions: [], auditEvents: [], evidence: [], agentDecisions: [], toolCallLogs: [] };
}
